import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const PersonalInfoSection = ({ userProfile, onUpdateProfile }) => {
  const [formData, setFormData] = useState({
    firstName: userProfile?.firstName || '',
    lastName: userProfile?.lastName || '',
    email: userProfile?.email || '',
    phone: userProfile?.phone || '',
    location: userProfile?.location || '',
    dateOfBirth: userProfile?.dateOfBirth || '',
    travelStyle: userProfile?.travelStyle || ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const locationOptions = [
    { value: 'california', label: 'California' },
    { value: 'texas', label: 'Texas' },
    { value: 'florida', label: 'Florida' },
    { value: 'new-york', label: 'New York' },
    { value: 'washington', label: 'Washington' },
    { value: 'colorado', label: 'Colorado' },
    { value: 'oregon', label: 'Oregon' },
    { value: 'arizona', label: 'Arizona' },
    { value: 'utah', label: 'Utah' },
    { value: 'montana', label: 'Montana' }
  ];

  const travelStyleOptions = [
    { value: 'adventure', label: 'Adventure Seeker' },
    { value: 'relaxed', label: 'Relaxed Explorer' },
    { value: 'family', label: 'Family Friendly' },
    { value: 'photographer', label: 'Nature Photographer' },
    { value: 'hiker', label: 'Hiking Enthusiast' },
    { value: 'camper', label: 'Camping Lover' }
  ];

  // Update formData when userProfile changes
  useEffect(() => {
    setFormData({
      firstName: userProfile?.firstName || '',
      lastName: userProfile?.lastName || '',
      email: userProfile?.email || '',
      phone: userProfile?.phone || '',
      location: userProfile?.location || '',
      dateOfBirth: userProfile?.dateOfBirth || '',
      travelStyle: userProfile?.travelStyle || ''
    });
  }, [userProfile]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onUpdateProfile(formData);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: userProfile?.firstName || '',
      lastName: userProfile?.lastName || '',
      email: userProfile?.email || '',
      phone: userProfile?.phone || '',
      location: userProfile?.location || '',
      dateOfBirth: userProfile?.dateOfBirth || '',
      travelStyle: userProfile?.travelStyle || ''
    });
    setIsEditing(false);
  };

  return (
    <div className="bg-card rounded-organic border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
            <Icon name="User" size={20} color="white" />
          </div>
          <div>
            <h3 className="font-heading font-heading-bold text-lg text-card-foreground">Personal Information</h3>
            <p className="font-caption text-sm text-muted-foreground">Manage your account details and preferences</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {isEditing && (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleSave}
                loading={isSaving}
                iconName="Save"
                iconPosition="left"
              >
                Save Changes
              </Button>
            </>
          )}
          {!isEditing && (
            <Button
              variant="outline"
              size="sm"
              iconName="Edit"
              iconPosition="left"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </Button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="First Name"
          type="text"
          value={formData?.firstName}
          onChange={(e) => handleInputChange('firstName', e?.target?.value)}
          disabled={!isEditing}
          required
        />

        <Input
          label="Last Name"
          type="text"
          value={formData?.lastName}
          onChange={(e) => handleInputChange('lastName', e?.target?.value)}
          disabled={!isEditing}
        />

        <Input
          label="Email Address"
          type="email"
          value={formData?.email}
          onChange={(e) => handleInputChange('email', e?.target?.value)}
          disabled={!isEditing}
          description="Used for trip notifications and account recovery"
          required
        />

        <Input
          label="Phone Number"
          type="tel"
          value={formData?.phone}
          onChange={(e) => handleInputChange('phone', e?.target?.value)}
          disabled={!isEditing}
          placeholder="(555) 123-4567"
        />

        <Select
          label="Primary Location"
          options={locationOptions}
          value={formData?.location}
          onChange={(value) => handleInputChange('location', value)}
          disabled={!isEditing}
          description="Helps us recommend nearby parks"
          placeholder="Select your state"
        />

        <Input
          label="Date of Birth"
          type="date"
          value={formData?.dateOfBirth}
          onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
          disabled={!isEditing}
        />

        <div className="md:col-span-2">
          <Select
            label="Travel Style"
            options={travelStyleOptions}
            value={formData?.travelStyle}
            onChange={(value) => handleInputChange('travelStyle', value)}
            disabled={!isEditing}
            description="This helps our AI create better trip recommendations for you"
            placeholder="Select your preferred travel style"
          />
        </div>
      </div>
      {isEditing && (
        <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-border">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            loading={isSaving}
            iconName="Save"
            iconPosition="left"
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};

export default PersonalInfoSection;